import { LOWER_IS_BETTER } from '../GscTools/metrics';
import { LONG_TAIL_MIN_WORDS, MODE, PRESET } from './scope';

/**
 * Visible English question words the question preset tests for.
 *
 * It is a text heuristic and nothing more: it reads the query string Search Console returned, in
 * the browser, after the fact. It does not know whether Google treated the query as a question,
 * and it has nothing to say about queries in other languages.
 */
const QUESTION_WORDS = new Set([
    'who',
    'what',
    'when',
    'where',
    'why',
    'how',
    'which',
    'whose',
    'whom',
    'can',
    'could',
    'do',
    'does',
    'did',
    'is',
    'are',
    'was',
    'were',
    'should',
    'will',
    'would',
]);

/** The words of a query, as the presets count them: whitespace-separated, punctuation kept. */
function words(text) {
    return (text ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean);
}

export function isQuestion(text) {
    const parts = words(text);
    return parts.some((word) => QUESTION_WORDS.has(word.replace(/[^a-z']/g, '')));
}

export function isLongTail(text) {
    return words(text).length >= LONG_TAIL_MIN_WORDS;
}

/** Applies the local text preset. A preset that does not apply to the view leaves rows alone. */
export function applyPreset(rows, { preset, presetApplies }) {
    if (!presetApplies) return rows;
    if (preset === PRESET.QUESTIONS) return rows.filter((row) => isQuestion(row.key));
    if (preset === PRESET.LONG_TAIL) return rows.filter((row) => isLongTail(row.key));
    return rows;
}

/**
 * The signed change in one metric for a joined row, or null where there is nothing to compare.
 *
 * The sign is the raw difference, not an opinion: for average position a negative change is an
 * improvement, which is why ordering asks `LOWER_IS_BETTER` which end of the scale a gain is on
 * rather than assuming bigger is better.
 */
export function movement(row, metric) {
    const current = row.current?.[metric];
    const prior = row.prior?.[metric];
    if (current === null || current === undefined) return null;
    if (prior === null || prior === undefined) return null;
    return current - prior;
}

/**
 * Orders and filters the joined rows for one mode.
 *
 * Popular ranks the current period's value, highest first — except for average position, where
 * the best value is the lowest. Gaining and Declining keep only rows with a comparable value in
 * both periods and rank them by how far the metric moved in the direction the mode is about, so a
 * row that cannot be compared is left out of a claim about gains rather than ranked as a zero.
 */
export function orderRows(rows, { mode, metric }) {
    const lowerIsBetter = !!LOWER_IS_BETTER[metric];

    if (mode === MODE.POPULAR) {
        const value = (row) => row.current?.[metric];
        return [...rows].sort((a, b) => {
            const left = value(a);
            const right = value(b);
            // Rows with no current value sort last whichever way the metric reads.
            if (left === null || left === undefined) return 1;
            if (right === null || right === undefined) return -1;
            if (left === right) return a.key < b.key ? -1 : 1;
            return lowerIsBetter ? left - right : right - left;
        });
    }

    const gaining = mode === MODE.GAINING;
    const improved = (delta) => (lowerIsBetter ? delta < 0 : delta > 0);

    const moved = rows
        .map((row) => ({ row, delta: movement(row, metric) }))
        .filter(({ delta }) => delta !== null && delta !== 0)
        .filter(({ delta }) => (gaining ? improved(delta) : !improved(delta)));

    moved.sort((a, b) => {
        const size = Math.abs(b.delta) - Math.abs(a.delta);
        if (size !== 0) return size;
        return a.row.key < b.row.key ? -1 : 1;
    });

    return moved.map(({ row }) => row);
}

/**
 * What the two cursors say about how much was retrieved and whether more can be asked for.
 *
 * The weaker of the two decides: a table can only show rows both periods have been read up to.
 * None of these states is evidence that the property has no further rows — Search Console
 * withholds rows below its own thresholds, so "no further rows returned" is the end of what it
 * will hand over, not the end of what exists.
 */
export function retrievalState(currentChunk, priorChunk) {
    const reasons = [currentChunk?.stopReason, priorChunk?.stopReason].filter(Boolean);

    let stopReason = 'empty_page';
    if (!reasons.length) stopReason = 'empty_page';
    else if (reasons.includes('more_available')) stopReason = 'more_available';
    else if (reasons.includes('product_cap')) stopReason = 'product_cap';

    return {
        stopReason,
        canLoadMore: stopReason === 'more_available',
        currentRows: currentChunk?.rows?.length ?? null,
        priorRows: priorChunk?.rows?.length ?? null,
        currentNextOffset: currentChunk?.nextOffset ?? null,
        priorNextOffset: priorChunk?.nextOffset ?? null,
    };
}
